import React from 'react';
import { Link } from 'react-router-dom';
import type { User } from '../../types';
import { Avatar } from '../ui/Avatar';
import { MaterialIcon } from '../ui/MaterialIcon';

interface ProCardProps {
  worker: User;
  /** Posición en el ranking (1-based); 1-3 muestra medalla, como ProCard en Flutter/Claude Design. */
  rank?: number;
}

const medalColors: Record<number, string> = {
  1: '#E6B325',
  2: '#A8A8A8',
  3: '#C17A3F',
};

// Calca ProCard de components.jsx (Claude Design)
export const ProCard: React.FC<ProCardProps> = ({ worker, rank }) => {
  const rating = Number(worker.rating_average) || 0;
  const medal = rank && rank <= 3 ? medalColors[rank] : undefined;

  return (
    <Link
      to={`/users/${worker.id}`}
      className="flex items-start gap-3 bg-surface border border-outlineVarWarm rounded-2xl p-3.5 hover:border-primary-300 hover:shadow-sm transition-all duration-200"
    >
      <div className="relative shrink-0">
        <Avatar src={worker.avatar} name={worker.full_name} size="lg" />
        {medal && (
          <span
            style={{ backgroundColor: medal }}
            className="absolute -top-1 -left-1 w-5 h-5 rounded-full border-2 border-surface text-[10px] font-bold text-white flex items-center justify-center"
          >
            {rank}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="text-[15px] font-bold text-onSurface truncate">{worker.full_name}</p>
          {worker.verification_status === 'approved' && (
            <MaterialIcon name="verified" size={16} fill className="text-info shrink-0" />
          )}
        </div>
        {worker.bio && <p className="text-[13px] text-onSurface-var line-clamp-2 mt-0.5">{worker.bio}</p>}

        <div className="flex items-center gap-3 mt-1.5 text-xs text-onSurface-mute">
          <span className="inline-flex items-center gap-1">
            <MaterialIcon name="star" size={13} fill className="text-warning-500" />
            <span className="font-semibold text-onSurface">{rating.toFixed(1)}</span>
            {typeof worker.reviews_count === 'number' && <span>({worker.reviews_count})</span>}
          </span>
          {worker.city && (
            <span className="inline-flex items-center gap-0.5">
              <MaterialIcon name="location_on" size={13} />
              {worker.city}
            </span>
          )}
        </div>

        {worker.skills && worker.skills.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {worker.skills.slice(0, 3).map((skill) => (
              <span key={skill.id} className="text-[11px] px-2 py-0.5 rounded-full bg-surface-dim text-onSurface-var">
                {skill.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
};
